import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Container } from "@/components/layout/Container";
import { StratoButton } from "@/components/common/StratoButton";
import { MicroLabel } from "@/components/common/MicroLabel";
import { Reveal } from "@/components/common/Reveal";
import { HERO } from "@/content/homeContent";
import { ANALYTICS_EVENTS, trackEvent } from "@/api/analyticsApi";

const SPECS = [
  { label: "TECNOLOGIA", value: "FDM" },
  { label: "MATERIALE", value: "PLA" },
  { label: "ANALISI", value: "Umana" },
];

export const Hero = () => {
  return (
    <section
      id="hero-section"
      data-testid="hero-section"
      className="relative overflow-hidden bg-strato-white pt-16 sm:pt-20 lg:pt-24"
    >
      <Container className="pb-16 lg:pb-24">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12">
          {/* Copy */}
          <div className="lg:col-span-8">
            <Reveal>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                {HERO.microLabels.map((label, i) => (
                  <React.Fragment key={label}>
                    {i > 0 ? (
                      <span className="h-1 w-1 rounded-full bg-strato-gray-300" />
                    ) : null}
                    <MicroLabel>{label}</MicroLabel>
                  </React.Fragment>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.08}>
              <h1 className="mt-6 font-display text-5xl font-medium leading-[1.02] tracking-[-0.03em] text-strato-graphite sm:text-6xl lg:text-[5rem]">
                Dal file<br className="hidden sm:block" /> al pezzo reale.
              </h1>
            </Reveal>

            <Reveal delay={0.16}>
              <p className="mt-7 max-w-[52ch] text-lg leading-[1.6] text-muted-foreground">
                {HERO.support}
              </p>
            </Reveal>

            <Reveal delay={0.24}>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
                <StratoButton
                  as={Link}
                  to="/preventivo"
                  data-testid="hero-start-quote-button"
                  onClick={() => trackEvent(ANALYTICS_EVENTS.QUOTE_CTA_CLICK, { button: "hero" })}
                  className="px-6 py-3.5 text-[15px]"
                >
                  Avvia il preventivo
                  <ArrowRight className="h-4 w-4" />
                </StratoButton>
                <StratoButton
                  as="a"
                  href="#process-section"
                  variant="secondary"
                  data-testid="hero-process-link"
                  className="px-6 py-3.5 text-[15px]"
                >
                  Scopri il processo
                </StratoButton>
              </div>
            </Reveal>

            <Reveal delay={0.3}>
              <p
                data-testid="hero-cta-note"
                className="mt-4 flex items-center gap-2 text-sm text-muted-foreground"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-strato-blue" />
                {HERO.ctaNote}
              </p>
            </Reveal>
          </div>

          {/* Lateral technical column */}
          <div className="lg:col-span-4">
            <Reveal delay={0.18}>
              <div
                data-testid="hero-tech-column"
                className="relative rounded-2xl border border-strato-gray-100 bg-strato-offwhite p-7"
              >
                <div className="flex items-center justify-between">
                  <MicroLabel variant="blue">SPECIFICHE</MicroLabel>
                  <span className="strato-micro text-strato-gray-300">STR / FDM</span>
                </div>
                <dl className="mt-6 divide-y divide-strato-gray-100">
                  {SPECS.map((s) => (
                    <div key={s.label} className="flex items-baseline justify-between gap-4 py-4 first:pt-0 last:pb-0">
                      <dt className="strato-micro text-muted-foreground">{s.label}</dt>
                      <dd className="font-display text-2xl font-medium tracking-[-0.01em] text-strato-graphite">
                        {s.value}
                      </dd>
                    </div>
                  ))}
                </dl>
                <div className="mt-6 flex items-center gap-2 border-t border-strato-gray-100 pt-5">
                  <span className="h-1.5 w-1.5 rounded-full bg-strato-blue" />
                  <span className="strato-micro text-muted-foreground">
                    PRODUZIONE CONTROLLATA
                  </span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </Container>
    </section>
  );
};
